<template>
  <div class="wt-asset-editor-view js-modal-content">
    <layout-general :header-height="50">
      <span slot="top-right-floating" />

      <div class="-header" slot="header">
        <span v-text="modalTitle" />
        <div class="-spring" />
        <ian-controls />
        <div class="-divider" />
        <wt-close class="-close" @click.native="close" />
      </div>

      <div
        v-if="!showLoader"
        class="-main"
        :class="{ '-webpage': isWebpage }"
        slot="main"
      >
        <wt-player-video v-if="hasVideoContent">
          <wt-volume-control group="video_modal">
            <video
              ref="video"
              playsinline
              :loop="shouldLoopVideo"
              preload="metadata"
              :poster="mixinSrc(workModel.metadata.posterUrl)"
              :src="mixinSrc(src)"
            />
          </wt-volume-control>
        </wt-player-video>
        <img v-else :src="mixinSrc(src)" />
      </div>

      <div
        v-show="showLoader"
        slot="loader"
        class="-loader"
        :class="{ '-failed': previewFailed }"
      />

      <info-bar :asset="workModel" slot="left" @close="close" />
      <div slot="bottom" class="-bottom">
        <img
          v-if="isKlipy"
          class="-powered-by-klipy"
          src="/resources/img/powered-by-klipy.png"
          alt="Powered by KLIPY"
        />
      </div>

      <footer v-if="isGallery" slot="footer" class="wt2-sg">
        <template v-if="isSelected">
          <wt-button type="primary" @click="close" text="Close" />
        </template>
        <template v-else>
          <wt-button type="transparent" @click="close" text="Cancel" />
          <wt-button type="primary" @click="add" text="Add" />
        </template>
      </footer>
      <footer v-else slot="footer" class="wt2-sg">
        <template v-if="canBackToVideo">
          <a class="wt2-color-link" @click.prevent="$_onBackToVideo"
            >Add another segment</a
          >
          <div class="-spring" />
        </template>
        <wt-button type="primary" @click="close" text="Close" />
      </footer>
    </layout-general>
  </div>
</template>

<script>
import LayoutGeneral from 'AssetEditor/layouts/LayoutGeneral.vue';
import IanControls from 'AssetEditor/functionality/InAssetNavigation/IanControls.vue';
import InfoBar from 'v/AssetPreview/InfoBar.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  getAssetModalTitle,
  getOriginalVideoFromFragment,
} from '../AssetEditor/store/assetEditorApi';
import app from 'app';
import { debounce } from 'lodash';
import { AssetJobObserver } from 'v/AssetPreview/assetPreviewApi';

export default {
  name: 'AssetPreview',
  components: {
    LayoutGeneral,
    IanControls,
    InfoBar,
  },

  computed: {
    ...mapState('assetEditor', ['openedFrom', 'workModel', 'initialModel']),
    ...mapGetters('assetEditor', ['canBackToVideo']),

    modalTitle() {
      return getAssetModalTitle(this.workModel);
    },

    shouldLoopVideo() {
      return this.isGif;
    },

    src() {
      const meta = this.workModel.metadata;

      if (this.hasVideoContent) {
        if (this.isGif) {
          return meta.url || meta.providerHiResAssetUrl;
        }
        return this.workModel.previewResultData?.cdnUrl;
      } else {
        if (this.isWebpage) {
          return meta.urlCdn;
        }
        if (this.isSocial) {
          return this.workModel._staticPreview;
        }
        return meta.urlCdn || meta.providerLowResAssetUrl;
      }
    },

    isGallery() {
      return this.openedFrom === 'asset-gallery';
    },

    showLoader() {
      return this.workModel._previewOp && this.workModel._previewOp !== 'done';
    },

    previewFailed() {
      return this.workModel._previewOp === 'fail';
    },

    isWebpage() {
      return app.assetType.isWEBPAGE(this.workModel);
    },

    isGif() {
      return app.assetType.isGIF(this.workModel);
    },

    isKlipy() {
      const providerId = this.workModel?.metadata?.providerId;
      return (
        typeof providerId === 'string' && providerId.toLowerCase() === 'klipy'
      );
    },

    isSocial() {
      return app.assetType.isSOCIAL(this.workModel);
    },

    hasVideoContent() {
      return (
        app.assetType.isFRAGMENT(this.workModel) ||
        app.assetType.isGIF(this.workModel) ||
        !!this.workModel.previewResultData
      );
    },

    isSelected() {
      return app.namespace.Controller.AssetController.isAssetSelected(
        this.workModel
      );
    },
  },

  beforeDestroy() {
    this.$options.assetJobObserver.stopListening();
  },

  created() {
    this.$options.assetJobObserver = new AssetJobObserver(this);
    this.$options.assetJobObserver.listen();
  },

  methods: {
    ...mapActions('assetEditor', ['track', 'NAVIGATE_TO_ASSET']),
    ...mapMutations('assetEditor', ['setOpenedFrom']),

    close() {
      this.$emit('close');
    },

    add() {
      app.rootStore.dispatch('ASSET_SELECTED', { asset: this.workModel });
      this.$emit('close');
    },

    $_onBackToVideo: debounce(
      function () {
        this.track({
          e: 'clickedOriginalVideo',
          params: { Source: 'Asset Preview' },
        });

        this.setOpenedFrom('original-video');
        const asset = getOriginalVideoFromFragment(this.workModel);
        this.NAVIGATE_TO_ASSET({ asset, suppressIan: true });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss" scoped>
.-header {
  height: 100%;
  padding-left: 20px;
  display: flex;
  align-items: center;

  .-divider {
    border-left: 1px solid rgba(0, 0, 0, 0.2);
    height: 100%;
  }

  .-close {
    width: 50px;
  }
}
.-spring {
  flex-grow: 1;
}
.-main {
  height: 100%;
}
footer {
  .wt2-color-link {
    margin: auto 0 auto 20px;
  }
}
.-webpage {
  overflow: auto;
  img {
    height: auto;
    object-fit: unset;
  }
}

.-loader {
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  position: relative;

  &.-failed:after {
    animation: none;
    content: 'Preview Failed';
    color: #e82566;
    width: auto;
  }
  &:after {
    width: 300px;
    content: 'Generating Preview';
    position: absolute;
    display: block;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-weight: 300;
    font-size: 30px;
    color: #fff;
    animation: rotatePreviewDots 3s infinite;
  }
}

img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: black;
}

.-bottom {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .-powered-by-klipy {
    width: auto;
    height: 16px;
    margin: 8px 0;
    object-fit: contain;
    background-color: transparent;
    pointer-events: none;
  }
}
</style>
